import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  Check,
  FileText,
  Globe2,
  Loader2,
  MessageSquareText,
  Sparkles,
  Target,
  Upload,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import {
  ingestWebsite,
  indexUploadedKnowledge,
  indexStoredDocument,
  addTextKnowledge,
} from "@/lib/knowledge.functions";
import { getMyCompany } from "@/lib/company.functions";
import { useHydratedTranslation } from "@/components/marketing/Layout";
const loadDocExtract = () => import("@/lib/document-extract");

import { buildSeo } from "@/lib/seo";

type TFn = ReturnType<typeof useHydratedTranslation>;

// Canonical (stored) value → i18n key. Values stay English in the DB so
// matching/embeddings and existing companies remain stable; only the display
// label is localized.
const IND_KEY: Record<string, string> = {
  SaaS: "indSaaS",
  Agency: "indAgency",
  "E-commerce": "indEcommerce",
  Consulting: "indConsulting",
  Finance: "indFinance",
  Healthcare: "indHealthcare",
  "Real estate": "indRealEstate",
  Other: "indOther",
};
const GOAL_KEY: Record<string, string> = {
  "Qualify leads": "goalQualify",
  "Answer support": "goalSupport",
  "Book calls": "goalBook",
  "Capture requests": "goalCapture",
  "Explain services": "goalExplain",
  "Recommend products": "goalRecommend",
};
const TONE_KEY: Record<string, string> = {
  Precise: "tonePrecise",
  Warm: "toneWarm",
  Premium: "tonePremium",
  Direct: "toneDirect",
};
const STEP_KEYS = [
  "stepCompanyName",
  "stepWebsite",
  "stepOffer",
  "stepBehavior",
  "stepKnowledge",
  "stepReview",
];
const labelWith = (t: TFn, map: Record<string, string>) => (v: string) =>
  t(`app.pages.onboarding.${map[v] ?? ""}`, { defaultValue: v });

export const Route = createFileRoute("/_authenticated/onboarding")({
  head: () =>
    buildSeo({
      path: "/onboarding",
      title: "Create your AI Twin — Reality Twin",
      description:
        "Set up your AI Twin in minutes — describe your company, connect your knowledge and go live with a Twin that qualifies leads on your behalf.",
      noindex: true,
    }),
  component: Onboarding,
});

type TwinTone = "Precise" | "Warm" | "Premium" | "Direct";

const industries = [
  "SaaS",
  "Agency",
  "E-commerce",
  "Consulting",
  "Finance",
  "Healthcare",
  "Real estate",
  "Other",
];
const companySizes = ["Solo", "2–10", "11–50", "51–200", "200+"];
const goals = [
  "Qualify leads",
  "Answer support",
  "Book calls",
  "Capture requests",
  "Explain services",
  "Recommend products",
];
const tones: TwinTone[] = ["Precise", "Warm", "Premium", "Direct"];

const stepMeta = [
  { label: "Company name", icon: Building2 },
  { label: "Website & market", icon: Globe2 },
  { label: "Offer & audience", icon: Target },
  { label: "Twin behavior", icon: MessageSquareText },
  { label: "Knowledge", icon: Upload },
  { label: "Review", icon: Sparkles },
];

function Onboarding() {
  const t = useHydratedTranslation();
  const indLabel = labelWith(t, IND_KEY);
  const goalLabel = labelWith(t, GOAL_KEY);
  const toneLabel = labelWith(t, TONE_KEY);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState(industries[0]);
  const [country, setCountry] = useState("Switzerland");
  const [size, setSize] = useState(companySizes[1]);
  const [audience, setAudience] = useState("");
  const [description, setDescription] = useState("");
  const [selectedGoals, setSelectedGoals] = useState<string[]>(["Qualify leads", "Answer support"]);
  const [tone, setTone] = useState<TwinTone>("Precise");
  const [handoff, setHandoff] = useState(t("app.pages.onboarding.handoffDefault"));
  const [files, setFiles] = useState<File[]>([]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  // Skip onboarding only if THIS user already has a company.
  //
  // This used to query `companies` unfiltered and take the first visible row.
  // But RLS intentionally exposes every public company (the marketplace needs
  // that), so a brand-new user got back a stranger's company, was sent to the
  // dashboard, which resolved *their own* company, found none, and sent them
  // back here — an endless redirect loop right after email verification.
  //
  // getMyCompany resolves through resolveCompany, exactly like the dashboard,
  // so both sides now answer "do I have a company?" the same way.
  const fetchMyCompany = useServerFn(getMyCompany);
  useEffect(() => {
    let cancelled = false;
    fetchMyCompany()
      .then((r) => {
        if (!cancelled && r.company) navigate({ to: "/dashboard", replace: true });
      })
      .catch(() => {
        // Staying on onboarding is the safe outcome — never bounce on error.
      });
    return () => {
      cancelled = true;
    };
  }, [fetchMyCompany, navigate]);

  const canContinue = useMemo(() => {
    if (step === 0) return companyName.trim().length > 1;
    if (step === 1)
      return website.trim().length > 3 && industry.length > 0 && country.trim().length > 1;
    if (step === 2)
      return (
        audience.trim().length > 4 && description.trim().length > 12 && selectedGoals.length > 0
      );
    if (step === 3) return tone.length > 0 && handoff.trim().length > 8;
    return true;
  }, [
    audience,
    companyName,
    country,
    description,
    handoff,
    industry,
    selectedGoals.length,
    step,
    tone,
    website,
  ]);

  function toggleGoal(goal: string) {
    setSelectedGoals((current) =>
      current.includes(goal) ? current.filter((item) => item !== goal) : [...current, goal],
    );
  }

  const runIngestWebsite = useServerFn(ingestWebsite);
  const runAddText = useServerFn(addTextKnowledge);
  const runIndexUpload = useServerFn(indexUploadedKnowledge);
  const runIndexStored = useServerFn(indexStoredDocument);

  async function finish() {
    setGenerating(true);
    setProgress(10);

    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      toast.error(t("app.pages.onboarding.signInAgain"));
      setGenerating(false);
      return;
    }

    const narrative = [
      description.trim(),
      `Audience: ${audience.trim()}.`,
      `Goals: ${selectedGoals.join(", ")}.`,
      `Tone: ${tone}.`,
      `Handoff: ${handoff.trim()}.`,
    ].join("\n");

    const websiteUrl = normalizeUrl(website);
    const { error } = await supabase.from("companies").insert({
      user_id: userData.user.id,
      name: companyName.trim(),
      website: websiteUrl,
      industry,
      country: country.trim(),
      size,
      description: narrative,
      personality: tone,
      seeking: selectedGoals,
      twin_status: "draft",
      knowledge_size: 0,
      documents_indexed: 0,
      pages_indexed: 0,
      ai_score: 20,
    });

    if (error) {
      toast.error(error.message);
      setGenerating(false);
      return;
    }

    setProgress(30);

    // Auto-index the company profile as knowledge so the twin can answer immediately
    try {
      await runAddText({
        data: {
          title: `${companyName.trim()} — Company profile`,
          content: `Company: ${companyName.trim()}\nIndustry: ${industry}\nMarket: ${country}\nSize: ${size}\nWebsite: ${websiteUrl}\n\n${narrative}`,
        },
      });
    } catch (e) {
      console.warn("Profile indexing failed", e);
    }
    setProgress(55);

    // Auto-crawl the website into the knowledge base
    if (websiteUrl) {
      try {
        await runIngestWebsite({ data: { url: websiteUrl } });
      } catch (e) {
        toast.warning(t("app.pages.onboarding.websiteWarn"));
      }
    }
    setProgress(80);

    // Upload, extract and index uploaded files. Failures are surfaced —
    // silently skipping a file means the twin never learns it.
    //
    // The extractor is a dynamic import, so it is only loaded when there is
    // actually a file to read: at this point the company already exists, and an
    // import failure must never strand the user on the generating screen.
    const failedFiles: string[] = [];
    const { extractDocumentText, safeStorageFileName } = files.length
      ? await loadDocExtract().catch(() => {
          failedFiles.push(...files.map((f) => f.name));
          return { extractDocumentText: null, safeStorageFileName: null } as any;
        })
      : ({ extractDocumentText: null, safeStorageFileName: null } as any);
    for (const file of extractDocumentText ? files : []) {
      try {
        const storagePath = `${userData.user.id}/${crypto.randomUUID()}-${safeStorageFileName(file.name)}`;
        const { error: uploadError } = await supabase.storage
          .from("knowledge")
          .upload(storagePath, file, {
            contentType: file.type || "application/octet-stream",
            upsert: false,
          });
        if (uploadError) throw uploadError;
        try {
          const text = await extractDocumentText(file);
          if (text.trim().length < 20) throw new Error("browser extraction too short");
          await runIndexUpload({
            data: { title: file.name, content: text, storagePath, sizeBytes: file.size },
          });
        } catch {
          // Browser could not read it — extract server-side from storage.
          await runIndexStored({ data: { title: file.name, storagePath, sizeBytes: file.size } });
        }
      } catch (e) {
        console.warn("File indexing failed", file.name, e);
        failedFiles.push(file.name);
      }
    }
    if (failedFiles.length > 0) {
      toast.warning(t("app.pages.onboarding.couldNotRead", { files: failedFiles.join(", ") }));
    }

    setProgress(100);
    await new Promise((resolve) => setTimeout(resolve, 320));
    toast.success(t("app.pages.onboarding.twinReady"));
    navigate({ to: "/dashboard" });
  }

  const Icon = stepMeta[step].icon;

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-6 pb-10 pt-24 sm:px-8 sm:pt-20">
        <section className="mx-auto w-full max-w-3xl">
          <div className="text-center">
            <p className="font-mono text-[12px] uppercase tracking-[0.32em] text-[oklch(0.74_0.18_166)]">
              {t("app.pages.onboarding.stepOf", { step: step + 1, total: stepMeta.length })}
            </p>
            <h1 className="mt-7 text-[38px] font-semibold leading-[0.98] tracking-[-0.03em] sm:text-[64px]">
              {generating
                ? t("app.pages.onboarding.buildingTitle")
                : t("app.pages.onboarding.buildTitle")}
            </h1>
          </div>

          <div className="mt-12 h-1.5 w-full overflow-hidden rounded-full bg-border sm:mt-14">
            <motion.div
              className="h-full rounded-full bg-foreground"
              animate={{
                width: `${generating ? progress : ((step + 1) / stepMeta.length) * 100}%`,
              }}
              transition={{ duration: 0.35 }}
            />
          </div>

          <div className="mt-12 rounded-[28px] border border-border bg-card p-6 shadow-[0_18px_70px_-46px_oklch(0_0_0_/_0.35)] sm:p-10">
            {generating ? (
              <div className="py-8 text-center">
                <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-foreground text-background">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
                <h2 className="mt-6 text-2xl font-semibold tracking-[-0.02em]">
                  {t("app.pages.onboarding.preparingTitle")}
                </h2>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-muted-foreground">
                  {t("app.pages.onboarding.preparingSub")}
                </p>
                <div className="mx-auto mt-7 h-2 max-w-sm overflow-hidden rounded-full bg-border">
                  <motion.div
                    className="h-full bg-foreground"
                    animate={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ) : (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <StepHeader icon={Icon} label={t(`app.pages.onboarding.${STEP_KEYS[step]}`)} />
                {step === 0 && (
                  <Question title={t("app.pages.onboarding.q0")}>
                    <TextInput
                      value={companyName}
                      onChange={setCompanyName}
                      placeholder={t("app.pages.onboarding.phCompanyName")}
                      autoFocus
                    />
                  </Question>
                )}

                {step === 1 && (
                  <Question title={t("app.pages.onboarding.q1")}>
                    <div className="space-y-4">
                      <TextInput
                        value={website}
                        onChange={setWebsite}
                        placeholder={t("app.pages.onboarding.phWebsite")}
                        autoFocus
                      />
                      <div className="grid gap-3 sm:grid-cols-2">
                        <SelectField
                          label={t("app.pages.onboarding.lblIndustry")}
                          value={industry}
                          options={industries}
                          onChange={setIndustry}
                          labelFor={indLabel}
                        />
                        <TextInput
                          value={country}
                          onChange={setCountry}
                          placeholder={t("app.pages.onboarding.phCountry")}
                        />
                      </div>
                      <ChipGroup value={size} values={companySizes} onChange={setSize} />
                    </div>
                  </Question>
                )}

                {step === 2 && (
                  <Question title={t("app.pages.onboarding.q2")}>
                    <div className="space-y-5">
                      <TextInput
                        value={audience}
                        onChange={setAudience}
                        placeholder={t("app.pages.onboarding.phAudience")}
                        autoFocus
                      />
                      <Textarea
                        value={description}
                        onChange={setDescription}
                        placeholder={t("app.pages.onboarding.phDescription")}
                      />
                      <MultiSelect
                        values={goals}
                        selected={selectedGoals}
                        onToggle={toggleGoal}
                        labelFor={goalLabel}
                      />
                    </div>
                  </Question>
                )}

                {step === 3 && (
                  <Question title={t("app.pages.onboarding.q3")}>
                    <div className="space-y-5">
                      <ChipGroup
                        value={tone}
                        values={tones}
                        onChange={(value) => setTone(value as TwinTone)}
                        labelFor={toneLabel}
                      />
                      <Textarea
                        value={handoff}
                        onChange={setHandoff}
                        placeholder={t("app.pages.onboarding.phHandoff")}
                      />
                    </div>
                  </Question>
                )}

                {step === 4 && (
                  <Question title={t("app.pages.onboarding.q4")}>
                    <label className="block cursor-pointer rounded-[22px] border border-dashed border-border bg-background/60 px-5 py-8 text-center transition hover:border-foreground/40">
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        accept=".pdf,.docx,.txt,.md,.markdown,.csv,.json,.html,.htm"
                        onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
                      />
                      <Upload className="mx-auto h-8 w-8 text-[oklch(0.74_0.18_166)]" />
                      <p className="mt-3 text-sm font-medium">
                        {t("app.pages.onboarding.uploadText")}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t("app.pages.onboarding.uploadHint")}
                      </p>
                    </label>
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {files.map((file) => (
                          <div
                            key={`${file.name}-${file.size}`}
                            className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-border px-3 py-2 text-sm"
                          >
                            <FileText className="h-4 w-4 shrink-0 text-[oklch(0.74_0.18_166)]" />
                            <span className="truncate">{file.name}</span>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {Math.max(1, Math.round(file.size / 1024))} KB
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Question>
                )}

                {step === 5 && (
                  <Question title={t("app.pages.onboarding.q5")}>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Summary
                        label={t("app.pages.onboarding.sumCompany")}
                        value={companyName}
                        icon={Building2}
                      />
                      <Summary
                        label={t("app.pages.onboarding.sumWebsite")}
                        value={normalizeUrl(website)}
                        icon={Globe2}
                      />
                      <Summary
                        label={t("app.pages.onboarding.sumIndustry")}
                        value={`${indLabel(industry)} · ${size}`}
                        icon={BriefcaseBusiness}
                      />
                      <Summary
                        label={t("app.pages.onboarding.sumMarket")}
                        value={country}
                        icon={Users}
                      />
                      <Summary
                        label={t("app.pages.onboarding.sumGoals")}
                        value={selectedGoals.map(goalLabel).join(", ")}
                        icon={Target}
                        wide
                      />
                      <Summary
                        label={t("app.pages.onboarding.sumTone")}
                        value={t("app.pages.onboarding.toneSummary", {
                          tone: toneLabel(tone),
                          count: files.length,
                        })}
                        icon={MessageSquareText}
                        wide
                      />
                    </div>
                  </Question>
                )}
              </motion.div>
            )}

            {!generating && (
              <div className="mt-9 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
                <button
                  type="button"
                  onClick={() => setStep((current) => Math.max(0, current - 1))}
                  disabled={step === 0}
                  className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground transition hover:text-foreground disabled:pointer-events-none disabled:opacity-25"
                >
                  <ArrowLeft className="h-4 w-4 shrink-0" />
                  <span className="truncate">{t("app.pages.onboarding.back")}</span>
                </button>
                {step < stepMeta.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => setStep((current) => current + 1)}
                    disabled={!canContinue}
                    className="inline-flex shrink-0 items-center gap-2 rounded-full bg-foreground px-7 py-3 text-[15px] font-medium text-background transition hover:opacity-90 disabled:opacity-35"
                  >
                    {t("app.pages.onboarding.continueBtn")} <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={finish}
                    className="inline-flex shrink-0 items-center gap-2 rounded-full bg-foreground px-7 py-3 text-[15px] font-medium text-background transition hover:opacity-90"
                  >
                    {t("app.pages.onboarding.generate")} <Sparkles className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function StepHeader({
  icon: Icon,
  label,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <div className="mb-5 flex min-w-0 items-center gap-3 font-mono text-[12px] uppercase tracking-[0.26em] text-[oklch(0.74_0.18_166)]">
      <Icon className="h-5 w-5 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

function Question({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-[25px] font-medium leading-tight tracking-[-0.02em] text-muted-foreground sm:text-[30px]">
        {title}
      </h2>
      <div className="mt-7">{children}</div>
    </div>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      autoFocus={autoFocus}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="w-full rounded-[20px] border border-border bg-background px-5 py-4 text-[22px] text-foreground outline-none transition placeholder:text-muted-foreground/55 focus:border-foreground/50 sm:text-[26px]"
    />
  );
}

function Textarea({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      rows={5}
      className="w-full resize-none rounded-[20px] border border-border bg-background px-5 py-4 text-[16px] leading-7 text-foreground outline-none transition placeholder:text-muted-foreground/55 focus:border-foreground/50"
    />
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
  labelFor,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  labelFor?: (value: string) => string;
}) {
  return (
    <label className="block">
      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-foreground/50"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {labelFor ? labelFor(option) : option}
          </option>
        ))}
      </select>
    </label>
  );
}

function ChipGroup({
  value,
  values,
  onChange,
  labelFor,
}: {
  value: string;
  values: readonly string[];
  onChange: (value: string) => void;
  labelFor?: (value: string) => string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {values.map((item) => (
        <button
          key={item}
          type="button"
          onClick={() => onChange(item)}
          className={`rounded-full border px-4 py-2 text-sm transition ${value === item ? "border-foreground bg-foreground text-background" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
        >
          {labelFor ? labelFor(item) : item}
        </button>
      ))}
    </div>
  );
}

function MultiSelect({
  values,
  selected,
  onToggle,
  labelFor,
}: {
  values: string[];
  selected: string[];
  onToggle: (value: string) => void;
  labelFor?: (value: string) => string;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {values.map((item) => {
        const active = selected.includes(item);
        return (
          <button
            key={item}
            type="button"
            onClick={() => onToggle(item)}
            className={`grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2 rounded-2xl border px-4 py-3 text-left text-sm transition ${active ? "border-foreground bg-foreground text-background" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
          >
            <span
              className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${active ? "border-background" : "border-border"}`}
            >
              {active && <Check className="h-3 w-3" />}
            </span>
            <span className="truncate">{labelFor ? labelFor(item) : item}</span>
          </button>
        );
      })}
    </div>
  );
}

function Summary({
  label,
  value,
  icon: Icon,
  wide = false,
}: {
  label: string;
  value: string;
  icon: ComponentType<{ className?: string }>;
  wide?: boolean;
}) {
  const t = useHydratedTranslation();
  return (
    <div
      className={`grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 rounded-2xl border border-border bg-background/70 p-4 ${wide ? "sm:col-span-2" : ""}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[oklch(0.74_0.18_166)]" />
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </div>
        <div className="mt-1 truncate text-sm font-medium">
          {value || t("app.pages.onboarding.notProvided")}
        </div>
      </div>
    </div>
  );
}

function normalizeUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}
